import { Component } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ApiEndpoint } from 'src/app/core/models/constants';
import { SessionAttribute, SessionService } from 'src/app/core/services/session.service';
import { VersionService } from 'src/app/core/services/version.service';
import { environment } from 'src/environments/environment';
import { catchError, map, of } from 'rxjs';
import { NotificationApiService } from 'src/app/core/services/notification-api.service';
import { SystemNotification } from 'src/app/core/models/system-notification';
import { LoadWheelService } from '../../load-wheel/load-wheel.service'
import { SpinnerProcess } from '../../../core/models/spinner-process';
import { ITimestampInfo } from 'src/app/core/services/serverdate.service';
import { SpellingService } from 'src/app/core/services/spelling.service';
import { SignalAppService } from 'src/app/core/services/signalapp.service';
import { MessageHandlerService } from '../../messages/message-handler/message-handler.service';
import { MessageType } from '../../messages/message-handler/message-handler.component';
import { StringUtils } from 'src/app/core/utils/string-utils';
import { AuthService } from 'src/app/auth/auth.service';
import { LlmAppService, WorksheetRequest, WorksheetResponse } from 'src/app/core/services/llmapp.service';

@Component({
   selector: 'app-developer-tools',
   standalone: false,
   templateUrl: './developer-tools.component.html',
   styleUrl: './developer-tools.component.scss',
})
export class DeveloperToolsComponent {

   envName: string = environment.environmentName;
   clientVersion: string = VersionService.getClientVersion();
   serverVersion!: string;

   serverName = '';

   personId!: number;
   userName!: string;
   sessionId!: string;

   clockDiscrepancy?: number;

   notifyText!: string;
   withToast = true;

   dialogText!: string;
   loaderText!: string;

   spellingNumwords!: string;
   
   accessibleScreens: Array<string> = ['(not loaded)'];
   accessibleRoles: Array<string> = ['(not loaded)'];

   constructor(
       private readonly http: HttpClient,
       private readonly versionService: VersionService,
       private readonly session: SessionService,
       private readonly authService: AuthService,
       private readonly notificationService: NotificationApiService,
       private readonly messageHandlerService: MessageHandlerService,
       private readonly loadWheelService: LoadWheelService,
       private readonly spellingService: SpellingService,
       private readonly signalAppService: SignalAppService,
       private readonly llmAppService: LlmAppService
   ) {
      this.refreshData();
   }

   refreshData() {
      this.serverVersion = this.versionService.getServerVersion();

      this.loadServerName();

      this.personId = this.session.getPersonId();
      this.userName = this.session.getUserName();
      this.sessionId = this.session.getSessionId();

      const sDate: ITimestampInfo = this.session.getAttribute(SessionAttribute.ServerDate);
      if (sDate != null) {
         this.clockDiscrepancy = sDate.cachedValue - sDate.cachedAt;
      }

      this.loadNumWords();
      
      this.authService.forDeveloperToolsOnlyAccessibleScreens().subscribe(screens => {
         this.accessibleScreens = screens.length > 0 ? screens.toSorted((a, b) => a.localeCompare(b)) : ['(none)'];
      })
      
      this.authService.forDeveloperToolsOnlyAccessibleRoles().subscribe(roles => {
         this.accessibleRoles = roles.length > 0 ? roles.toSorted((a, b) => a.localeCompare(b)) : ['(none)'];
      })
   }

   private loadServerName() {
      this.http.get<string>(environment.apiUrl + ApiEndpoint.ServerName)
         .pipe(
            map((data: any) => data.response),
            catchError((err) => {
               console.warn(`Server name lookup failed: (${err.status}) ${err.message}`);
               return of('(error)');
            })
         ).subscribe(response => this.serverName = response);

   }

   private loadNumWords() {
      const numWords = this.spellingService.getWordCount();
      this.spellingNumwords = numWords ? StringUtils.applyNumberThousandsFormat(numWords.toString()) + ' words' : '(not loaded)';
   }

   testNotification() {
      this.notificationService.notify('Test Notification', new SystemNotification(this.notifyText), this.withToast);
   }

   testDialog(type: string) {
      switch (type) {
         case 'INFO':
            this.messageHandlerService.show(this.dialogText, MessageType.INFO);
            break;
         case 'ERROR':
            this.messageHandlerService.show(this.dialogText, MessageType.ERROR);
            break;
         case 'EXCEPTION': {
            const data = {
               exception: 'com.example.Test',
               message: this.dialogText,
               stack: 'No stack trace, this is just a test!',
               errorTrackingCode: '9876543210:test',
            }
            this.messageHandlerService.showExceptionDialog(data);
            break;
         }
      }
   }

   testLoader(millis: number) {
      const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.loaderText);
      setTimeout(() => this.loadWheelService.hideWheel(wheel), millis);
   }

   testLoaderMultiple(millis: number, qty: number, offset: number) {
      if(qty < 2)
      {
         this.testLoader(millis);
         return;
      }

      for(let i = 0; i < qty;i++)
      {
         const jitter = Math.floor(Math.random() * (millis / 3));
         setTimeout(() => {
            const wheel: SpinnerProcess = this.loadWheelService.showWheel(`${this.loaderText} (#${i+1}, ${jitter} jitter)`);
            setTimeout(() => this.loadWheelService.hideWheel(wheel), millis + jitter);
         }, (i * offset) + jitter);
      }
   }

   clearIIRDictionaryCache() {
      this.spellingService.clearIIRDictionaryCache();
      this.loadNumWords();
   }
   
   testSignalToken(allowCached: boolean) {
      if(!allowCached) {
         this.authService.invalidateSignalTokenCache();
      }

      this.authService.getSignalToken().subscribe(token => {
          this.messageHandlerService.show(token, MessageType.INFO);
      });
   }   

   testSignalQuery() {
      this.signalAppService.runSignalQuery({entity_type: "Plant", state: "Texas"});
   }
   
   testLlmToken(allowCached: boolean) {
      if(!allowCached) {
         this.authService.invalidateLlmTokenCache();
      }

      this.authService.getLlmToken().subscribe(token => {
         this.messageHandlerService.show(token, MessageType.INFO);
      });
   }   

   testLlmRgcUrl() {
      this.llmAppService.getRgcEmbedUrl().subscribe(url => {
         this.messageHandlerService.show(url, MessageType.INFO);
      });
   }

   testLlmRaApi() {
      
       const scopeExample: WorksheetRequest = {
           "fields": {
               "pec_activity": "Preliminary Engineering",
               "project_status": "Active",
               "tiv": "100000000",
               "tiv_currency": "USD",
               "unit_ids": [
                   2506390
               ]
           },
           "offline_mode": true,
           "output": "scope",
           "project_id": 300055833,
           "plant_id": 1000241,
           "project_type": "Grassroot",
           "selected_key_needs": {
               "kn": {
                   "Electrical": 1,
                   "Mechanical": 1
               }
           },
           "sic_product_id": "4931*0003"
       };
       
       const scheduleExample: WorksheetRequest = {
           "fields": {
               "afe_date": "2027/Jan",
               "bid_doc_date": "202707",
               "completion_date": "202801",
               "kickoff_date": "2027/Oct",
               "pec_activity": "Preliminary Engineering",
               "project_status": "Active",
               "rfq_date": "Q2 2027"
           },
           "offline_mode": true,
           "output": "schedule",
           "project_id": 300015362,
           "plant_id": 1000241,
           "project_type": "Grassroot",
           "sic_product_id": "2911*0015"
       };

       const keyNeedsExample: WorksheetRequest = {
           "fields": {
               "unit_ids": [
                   2506390
               ]
           },
           "offline_mode": true,
           "output": "key_needs",
           "project_id": 300088509,
           "plant_id": 1000241,
           "project_type": "Grassroot",
           "sic_product_id": "4931*0003"
       }

       const testRequest: WorksheetRequest = [scopeExample, scheduleExample, keyNeedsExample][Math.floor(Math.random() * 3)]; // Just pick one at random
       console.debug('RARequest', testRequest);
       
       this.llmAppService.researchAssistant(testRequest).subscribe((result: WorksheetResponse) => {
           console.debug('RAResponse', result);

           const message = {
               scope: result.project_scope,
               schedule: result.project_schedule,
               key_needs: JSON.stringify(result.key_needs)
           }[testRequest.output];

           this.messageHandlerService.show(`${testRequest.output}:\n\n${message}`, MessageType.INFO);
       });
   }
}
